import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { AnimalDTO, AnimalPayload } from "../types/dtos";

export type SpeciesOption = { id: number; label: string; code?: string };
export type GenderOption = { id: number; label: string; code?: string };
export type SizeOption = { id: number; label: string; code?: string };

export const animalsApi = createApi({
  reducerPath: "animalsApi",
  baseQuery: fetchBaseQuery({ baseUrl: "/api" }),
  tagTypes: ["Animals", "Species", "Sizes", "Bookings", "Genders"],
  endpoints: (b) => ({
    // Existing
    getAnimalsForCustomerRaw: b.query<AnimalDTO[], number>({
      query: (customerId) => ({ url: `animals/allAnimals/${customerId}` }),
      providesTags: (res, _err, customerId) =>
        res
          ? [
              ...res.map((a) => ({ type: "Animals" as const, id: a.AutoID })),
              { type: "Animals", id: `LIST:${customerId}` },
            ]
          : [{ type: "Animals", id: `LIST:${customerId}` }],
    }),

    // Check if an animal has invoices in draft
    checkHasInvoiceDraftForAnimal: b.query<boolean, number>({
      query: (id) => ({ url: `animals/hasinvoiceindraft/${id}/` }),
    }),

    // Details
    getAnimalById: b.query<AnimalDTO, number>({
      query: (id) => ({ url: `animals/animal/${id}/` }),
      providesTags: (_res, _err, id) => [{ type: "Animals", id }],
    }),

    // Options
    getSpecies: b.query<SpeciesOption[], { siteId?: number } | void>({
      query: () => ({ url: `animals/species/` }),
      transformResponse: (res: any): SpeciesOption[] => {
        // Accept both array of objects or wrapped shapes
        const arr = Array.isArray(res) ? res : res?.data ?? [];
        return arr.map((s: any) => ({
          id: Number(s.AutoID ?? s.id),
          label: s.Name1 ?? s.label,
          code: s.Code ?? s.code,
        }));
      },
      providesTags: [{ type: "Species", id: "LIST" }],
    }),

    getSizesForSpecies: b.query<SizeOption[], number>({
      query: (speciesId) => ({ url: `animals/animalSizes/${speciesId}/` }),
      transformResponse: (res: any): SizeOption[] => {
        const arr = Array.isArray(res) ? res : res?.data ?? [];
        return arr.map((it: any) => ({
          id: Number(it.AutoID ?? it.id),
          label: it.Name1 ?? it.label,
          code: it.Code ?? it.code,
        }));
      },
      providesTags: (_res, _err, speciesId) => [
        { type: "Sizes", id: `LIST:${speciesId}` },
      ],
    }),

    getGenders: b.query<GenderOption[], void>({
      query: () => ({ url: `animals/genders/` }),
      transformResponse: (res: any): GenderOption[] => {
        const arr = Array.isArray(res) ? res : res?.data ?? [];
        return arr.map((g: any) => ({
          id: Number(g.AutoID ?? g.id),
          label: g.Name1 ?? g.label ?? g.Code ?? g.code ?? "",
          code: g.Code ?? g.code,
        }));
      },
      providesTags: [{ type: "Genders", id: "LIST" }],
    }),

    // Options for selector: animals of a customer
    getAllAnimalsForCustomer: b.query<
      { id: number; label: string; otherData: AnimalDTO; disabled?: boolean }[],
      { customerId: string; includeEx?: boolean } | string
    >({
      query: (arg) => {
        const customerId = typeof arg === "string" ? arg : arg.customerId;
        const includeEx =
          typeof arg === "string" ? false : Boolean(arg.includeEx);
        const params = includeEx ? "?includeEx=1" : "";
        return { url: `animals/allAnimals/${customerId}${params}` };
      },
      transformResponse: (response: any[]) =>
        (response ?? []).map((animal: any) => ({
          id: animal.AutoID,
          label: animal.Name1 ?? "",
          otherData: animal,
          disabled: Boolean(animal?.ex),
        })),
      providesTags: (res, _err, arg) => {
        const customerId = typeof arg === "string" ? arg : arg.customerId;
        return res
          ? [
              { type: "Animals", id: `LIST:${customerId}` },
              ...res.map((a) => ({ type: "Animals" as const, id: a.id })),
            ]
          : [{ type: "Animals", id: `LIST:${customerId}` }];
      },
    }),

    // Utility: species by animal IDs
    getSpeciesForAnimalIDs: b.query<number[], number[]>({
      query: (animalIDs) => {
        const queryString = animalIDs.join(",");
        return { url: `animals/species/animalIDs`, params: { animalIDs: queryString } } as any;
      },
    }),

    // Mutations
    createAnimal: b.mutation<AnimalDTO, AnimalPayload>({
      query: (body) => ({ url: `animals/`, method: "POST", body }),
      invalidatesTags: (_res, _err, body) => [
        { type: "Animals", id: `LIST:${body?.Customer_ID ?? "*"}` },
      ],
    }),

    updateAnimal: b.mutation<AnimalDTO, { id: number; patch: AnimalPayload }>({
      query: ({ id, patch }) => ({
        url: `animals/animal/${id}/`,
        method: "PATCH",
        body: patch,
      }),
      invalidatesTags: (res, _err, { id, patch }) => {
        const tags: { type: "Animals" | "Bookings" | "Species"; id: string | number }[] = [
          { type: "Animals", id },
        ];
        const customerId = res?.Customer_ID ?? patch?.Customer_ID ?? null;
        if (customerId != null) {
          tags.push({ type: "Animals", id: `LIST:${customerId}` });
        }
        tags.push({ type: "Animals", id: "LIST" });
        tags.push({ type: "Bookings", id: "LIST" });
        tags.push({ type: "Bookings", id: "CAPACITY" });
        tags.push({ type: "Bookings", id: "SPACE_CAPACITY" });
        return tags;
      },
    }),

    restoreAnimal: b.mutation<
      { AutoID: number; ex: boolean; Customer_ID?: number | null },
      number
    >({
      query: (id) => ({
        url: `animals/animal/${id}/restore`,
        method: "POST",
      }),
      invalidatesTags: (res, _err, id) => {
        const tags: { type: "Animals"; id: string | number }[] = [
          { type: "Animals", id },
        ];
        const customerId = res?.Customer_ID;
        if (customerId != null) {
          tags.push({ type: "Animals", id: `LIST:${customerId}` });
        }
        return tags;
      },
    }),

    deleteAnimal: b.mutation<
      { success: boolean; hasBookings?: boolean },
      { id: number; customerId?: number }
    >({
      query: ({ id }) => ({
        url: `animals/animal/${id}/`,
        method: "DELETE",
      }),
      invalidatesTags: (_res, _err, { id, customerId }) => {
        const tags: { type: "Animals"; id: number | string }[] = [
          { type: "Animals", id },
        ];
        if (customerId != null) {
          tags.push({ type: "Animals", id: `LIST:${customerId}` });
        }
        return tags;
      },
    }),
  }),
});

export const {
  useGetAnimalsForCustomerRawQuery,
  useGetAnimalByIdQuery,
  useGetSpeciesQuery,
  useGetSizesForSpeciesQuery,
  useGetGendersQuery,
  useGetAllAnimalsForCustomerQuery,
  useGetSpeciesForAnimalIDsQuery,
  useCreateAnimalMutation,
  useUpdateAnimalMutation,
  useRestoreAnimalMutation,
  useCheckHasInvoiceDraftForAnimalQuery,
  useLazyCheckHasInvoiceDraftForAnimalQuery,
  useDeleteAnimalMutation,
} = animalsApi;
